'use client';

import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import {
  PayPalScriptProvider,
  PayPalButtons as PayPalButtonsSdk,
} from '@paypal/react-paypal-js';
import { useToast } from '@/src/components/ui';

type Props = {
  amount: string;
  currency: string;
  productName: string;
  creatorId: number;
  profileUniqueLink: string;
  message?: string;
  onSuccess: () => void;
  onCancel: () => void;
  onError: (e: unknown) => void;
};

export function PayPalButtons(props: Props) {
  const {
    amount,
    currency,
    productName,
    creatorId,
    profileUniqueLink,
    message,
    onSuccess,
    onCancel,
    onError,
  } = props;

  const toast = useToast();

  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const setDonateQuery = (status: 'success' | 'cancel', paymentId?: string) => {
    const params = new URLSearchParams(searchParams.toString());

    params.delete('subscribe');
    params.delete('donate');
    params.delete('payment_id');

    params.set('donate', status);
    if (paymentId) params.set('payment_id', paymentId);

    router.replace(`${pathname}?${params.toString()}`);
  };

  const clientId = process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID!;
  const options = {
    clientId,
    currency,
    intent: 'capture',
    components: 'buttons',
  } as const;

  return (
    <PayPalScriptProvider options={options}>
      <PayPalButtonsSdk
        style={{ layout: 'vertical' }}
        createOrder={async () => {
          const res = await fetch('/api/paypal/orders', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              amount,
              currency,
              productName,
              paymentType: 'donation',
              creatorId,
              donationMessage: message ?? '',
              profileUniqueLink,
            }),
          });

          const json = await res.json();
          if (!json.ok) throw new Error(json.error || 'Failed to create order');

          return json.orderId as string;
        }}
        onApprove={async (data, actions) => {
          const orderId = data.orderID;

          const res = await fetch(`/api/paypal/orders/${orderId}/capture`, {
            method: 'POST',
          });

          const json = await res.json();
          const issue =
            json?.error?.details?.[0]?.issue ??
            json?.error?.name ??
            json?.error?.error?.details?.[0]?.issue;

          if (
            issue === 'INSTRUMENT_DECLINED' ||
            issue === 'TRANSACTION_REFUSED'
          ) {
            return actions?.restart();
          }

          if (!json.ok) throw new Error(json.error || 'Failed to capture');

          const paymentId = String(json.paymentId ?? json.draftId ?? '');

          toast.success('Payment captured!');
          setDonateQuery('success', paymentId || undefined);
          onSuccess();
        }}
        onCancel={() => {
          toast.error('Payment cancelled');
          setDonateQuery('cancel');
          onCancel();
        }}
        onError={(err) => {
          console.error(err);
          toast.error('PayPal error');
          onError(err);
        }}
      />
    </PayPalScriptProvider>
  );
}
